/**
 * Custom Field Types Configuration
 * 
 * Defines all available custom field types and their properties.
 * Each type specifies how values are stored, validated, and rendered.
 */

export const CUSTOM_FIELD_TYPES = {
  text: {
    id: 'text',
    label: 'Text',
    icon: 'Aa',
    description: 'Single line of text',
    defaultValue: '',
    placeholder: 'Enter text...',
  },
  number: {
    id: 'number',
    label: 'Number',
    icon: '#',
    description: 'Numeric value',
    defaultValue: null,
    placeholder: '0',
  },
  checkbox: {
    id: 'checkbox',
    label: 'Checkbox',
    icon: '☑',
    description: 'Yes or no toggle',
    defaultValue: false,
  },
  select: {
    id: 'select',
    label: 'Dropdown Menu',
    icon: '▾',
    description: 'Choose from a list',
    defaultValue: null,
    // Select fields require options array: [{ id, label, color }]
    requiresOptions: true,
  },
  date: {
    id: 'date',
    label: 'Date',
    icon: '📅',
    description: 'Calendar date',
    defaultValue: null,
    placeholder: 'Pick a date...',
  },
  url: {
    id: 'url',
    label: 'URL',
    icon: '🔗',
    description: 'Web link',
    defaultValue: '',
    placeholder: 'https://...',
  },
};

/**
 * Get field type config by ID
 */
export const getFieldType = (typeId) => CUSTOM_FIELD_TYPES[typeId] || null;

/**
 * Get all field types as array (for rendering in UI)
 */
export const getFieldTypesList = () => Object.values(CUSTOM_FIELD_TYPES);

/**
 * Validate a value against its field type
 */
export const validateFieldValue = (value, fieldType, field) => {
  if (value === null || value === undefined || value === '') {
    return { valid: true, value: null }; // Empty is always valid (unless required)
  }

  switch (fieldType) {
    case 'text':
      return { valid: typeof value === 'string', value: String(value) };
    
    case 'number':
      const num = Number(value);
      return { valid: !isNaN(num), value: isNaN(num) ? null : num };
    
    case 'checkbox':
      return { valid: typeof value === 'boolean', value: Boolean(value) };
    
    case 'select':
      // Value should be an option ID that exists in field.options
      const validOption = field?.options?.some(opt => opt.id === value);
      return { valid: validOption, value: validOption ? value : null };
    
    case 'date':
      const date = new Date(value);
      const isValidDate = !isNaN(date.getTime());
      return { valid: isValidDate, value: isValidDate ? value : null };
    
    case 'url':
      try {
        new URL(value);
        return { valid: true, value };
      } catch {
        return { valid: false, value: null };
      }
    
    default:
      return { valid: true, value };
  }
};

/**
 * Generate a unique field ID
 */
export const generateFieldId = () => `cf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Generate a unique option ID (for select fields)
 */
export const generateOptionId = () => `opt-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Default colors for select options
 */
export const DEFAULT_OPTION_COLORS = [
  '#ef4444', // red
  '#f59e0b', // amber
  '#10b981', // emerald
  '#3b82f6', // blue
  '#8b5cf6', // violet
  '#ec4899', // pink
  '#6b7280', // gray
  '#14b8a6', // teal
];

/**
 * Get next default color for a new option
 */
export const getNextOptionColor = (existingOptions = []) => {
  const usedColors = existingOptions.map(opt => opt.color);
  const availableColor = DEFAULT_OPTION_COLORS.find(c => !usedColors.includes(c));
  return availableColor || DEFAULT_OPTION_COLORS[existingOptions.length % DEFAULT_OPTION_COLORS.length];
};

export default {
  CUSTOM_FIELD_TYPES,
  getFieldType,
  getFieldTypesList,
  validateFieldValue,
  generateFieldId,
  generateOptionId,
  DEFAULT_OPTION_COLORS,
  getNextOptionColor,
};
